import { useEffect, useRef, useState } from 'react';

import { detectCountryWithCache, getLocaleCountry, isSupportedCountry } from '../utils/geo_country';

const USER_COUNTRY_STORAGE_KEY = 'user_country_override';
const USER_COUNTRY_TTL_30_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const safeSetLocalStorage = (key, value) => {
  try {
    window.localStorage?.setItem(key, value);
    return true;
  } catch (_error) {
    return false;
  }
};

function readStoredCountry() {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage?.getItem(USER_COUNTRY_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const { country, ts } = parsed || {};
    if (!isSupportedCountry(country) || typeof ts !== 'number') return undefined;
    const isFresh = Date.now() - ts < USER_COUNTRY_TTL_30_DAYS_MS;
    return isFresh ? country : undefined;
  } catch (_error) {
    return undefined;
  }
}

function persistUserCountry(country) {
  if (typeof window === 'undefined') return;
  if (!isSupportedCountry(country)) return;
  const payload = JSON.stringify({ country, ts: Date.now() });
  safeSetLocalStorage(USER_COUNTRY_STORAGE_KEY, payload);
}

export function useGeoCountry({ defaultCountry, disableAuto = false }) {
  const normalizedDefaultCountry = (() => {
    const candidate = typeof defaultCountry === 'string' ? defaultCountry.toUpperCase() : undefined;
    if (!candidate || candidate === 'INTERNATIONAL') return undefined;
    return isSupportedCountry(candidate) ? candidate : undefined;
  })();

  const storedCountryRef = useRef(readStoredCountry());

  const [country, setCountry] = useState(storedCountryRef.current || normalizedDefaultCountry);
  const userCountryChangeRef = useRef(false);

  useEffect(() => {
    if (disableAuto || storedCountryRef.current) return;
    let canceled = false;
    const resolveCountry = async () => {
      if (userCountryChangeRef.current) return;
      const ipCountry = await detectCountryWithCache();
      if (canceled) return;
      const localeCountry = getLocaleCountry();
      const candidate = ipCountry || localeCountry || normalizedDefaultCountry;
      if (!candidate) return;
      if (candidate !== country) setCountry(candidate);
    };
    resolveCountry();
    return () => {
      canceled = true;
    };
  }, [country, disableAuto, normalizedDefaultCountry]);

  useEffect(() => {
    if (!userCountryChangeRef.current) return;
    if (!country) return;
    persistUserCountry(country);
  }, [country]);

  const markUserCountryChange = () => {
    userCountryChangeRef.current = true;
  };

  return { country, setCountry, normalizedDefaultCountry, markUserCountryChange };
}
